import { useEffect, useRef, useState } from 'react';
import { useActionCable } from '~/providers';

interface AIAnswerContent {
  id: string;
  title: string;
  content_type: 'guide' | 'resource';
  description?: string;
  slug?: string;
  [key: string]: any;
}

interface UseAIAnswerWithSubscriptionOptions {
  question?: string;
  userId?: number;
  apiCall: (question: string) => Promise<any>;
  enabled?: boolean;
  channelName?: string;
  channelParams?: Record<string, any>;
  minQuestionLength?: number;
}

export const useAIAnswerWithSubscription = ({
  question,
  userId,
  apiCall,
  enabled = true,
  channelName = 'LearnerSearchAnswers::QuestionAnswerChannel',
  channelParams = {},
  minQuestionLength = 0,
}: UseAIAnswerWithSubscriptionOptions) => {
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [content, setContent] = useState<AIAnswerContent[]>([]);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [isSubscriptionReady, setIsSubscriptionReady] = useState(false);

  const previousQuestionRef = useRef<string | undefined>();
  const currentQuestionRef = useRef<string | undefined>();
  const channelParamsRef = useRef<string>(JSON.stringify(channelParams));
  const consumer = useActionCable();

  useEffect(() => {
    currentQuestionRef.current = question;
  }, [question]);

  useEffect(() => {
    if (!enabled || !question || !userId) return;
    if (question.length < minQuestionLength) return;
    if (question === previousQuestionRef.current) return;

    if (!isSubscriptionReady) {
      return;
    }

    previousQuestionRef.current = question;
    setLoading(true);
    setProgressMessage(null);
    apiCall(question);
  }, [question, userId, enabled, apiCall, minQuestionLength, isSubscriptionReady]);

  useEffect(() => {
    if (!enabled || !userId) return undefined;

    const currentParamsJson = JSON.stringify(channelParams);
    if (currentParamsJson !== channelParamsRef.current) {
      channelParamsRef.current = currentParamsJson;
    }

    const subscription = consumer.subscriptions.create(
      {
        channel: channelName,
        user_id: userId,
        ...channelParams,
      },
      {
        connected: () => {
          setIsSubscriptionReady(true);
        },
        disconnected: () => {
          setIsSubscriptionReady(false);
        },
        received: (resp) => {
          if (resp.type === 'progress') {
            setProgressMessage(resp.message);
            return;
          }

          const { answer: newAnswer, answered, question: answeredQuestion, courses, resources } = resp;

          if (answeredQuestion !== currentQuestionRef.current) {
            return;
          }

          setLoading(false);
          setProgressMessage(null);
          setAnswer(answered ? newAnswer : null);
          setContent([
            ...courses.map((c: any) => ({ ...c, content_type: 'guide' })),
            ...(resources || []).map((r: any) => ({ ...r, content_type: 'resource' })),
          ]);
        },
      },
    );

    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, enabled, channelName, consumer, channelParamsRef.current]);

  return { loading, answer, content, progressMessage };
};
